import React, { useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ButtonLoading } from "go-form";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { FormDirty } from "go-form/components/FormDirty";
import { FormSelectGroup } from "go-form/components/FormSelect";
import { FILTER_VALUE_SEPARATOR } from "go-list/core/components/Filter/services";
import { apiOrganization } from "../../../../../../../../../../../services/Api/Organization/apiOrganization";
import { InvoiceApi, OrderSearchSelectApi } from "../../../../../../../../../../../services/Api/Organization/types";

interface Props {
	show: boolean;
	onHide: () => void;
	invoice: InvoiceApi;
	setInvoice: (invoice: InvoiceApi) => void;
}

const InvoicePreviewOrdersModal = ({ show, onHide, invoice, setInvoice }: Props) => {
	const { t } = useTranslation();
	const [loading, setLoading] = useState(false);
	const form = useForm<InvoiceApi>({
		defaultValues: invoice,
		criteriaMode: "all",
	});
	const {
		handleSubmit,
		setError,
		control,
		formState,
		formState: { errors },
		setValue,
	} = form;
	const { addFlash, addSuccessFlash } = useFlash();
	const [searchByRecipient, setSearchByRecipient] = useState(false);

	const searchOrders = async (search: string, params: Record<string, any>, options?: Record<string, any>) => {
		let finalParams: Record<string, any> = {
			...params,
			"statuses|e": `SHIPPED${FILTER_VALUE_SEPARATOR}PARTIALLY_SHIPPED${FILTER_VALUE_SEPARATOR}OPEN`,
			"invoiceIds|u": "",
		};
		if (searchByRecipient) {
			finalParams = {
				...finalParams,
				"contactIds|e": invoice.recipient_id,
			};
		}

		return apiOrganization.getOrderSearchSelect(search, finalParams, {
			cancelToken: options?.token,
		});
	};

	const onSubmit = handleSubmit(async (data) => {
		setLoading(true);
		try {
			const res = await apiOrganization.updateInvoice(invoice.id, data, { include: "all" });
			setInvoice(res);
			onHide();
			addSuccessFlash(t("lib:common.flash.saved"));
		} catch (err) {
			handleError.form(err, setError, addFlash);
		}
		setLoading(false);
	});

	const handleChangeOrders = (value: OrderSearchSelectApi[]) => {
		setValue(
			"orders",
			value.map((item) => {
				const invoiceOrder = invoice.orders.find(({ id }) => Number(id) === Number(item.id));
				return {
					id: Number(item.id),
					order_id: invoiceOrder?.order_id ? Number(invoiceOrder.order_id) : Number(item.id),
					order_number: item.label,
					order_tax_id_no: "",
					order_sale_date: item.order_at,
				};
			})
		);
	};

	return (
		<Modal show={show} onHide={onHide}>
			<Modal.Header closeButton>
				<Modal.Title>{t("modules.invoice.action.add_orders.title")}</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<FormDirty formState={formState} onSubmit={onSubmit}>
					<Form.Check
						label={t("modules.invoice.field.search_by_recipient.title")}
						className="form-check"
						checked={searchByRecipient}
						onChange={() => setSearchByRecipient((prevState) => !prevState)}
						id="search-by-recipient"
					/>
					<span className="text-muted d-inline-block my-3">
						{t("modules.invoice.field.choose_orders.title")}
					</span>
					<FormSelectGroup
						name="orders"
						errors={errors}
						control={control}
						loadOptions={searchOrders}
						isMulti
						getOptionValue={(option) => Number(option.id)}
						defaultValue={invoice.orders.map((order) => ({
							id: order.id,
							label: order.order_number,
						}))}
						onChange={(_, fullObj) => handleChangeOrders(fullObj)}
						isClearable={false}
						cacheUniqs={[searchByRecipient]}
						data-testid="orders"
					/>
				</FormDirty>
			</Modal.Body>
			<Modal.Footer>
				<ButtonLoading loading={loading} type="submit" onClick={onSubmit}>
					{t("lib:common.action.save")}
				</ButtonLoading>
				<Button variant="light" onClick={onHide}>
					{t("lib:common.action.cancel")}
				</Button>
			</Modal.Footer>
		</Modal>
	);
};

export default InvoicePreviewOrdersModal;
